import React, { createRef, useEffect } from 'react'
import { EmbedCode } from './EmbedCode'
import Logo from '../images/rula_logo_white.svg'
import parse from 'html-react-parser'
import * as E from '../elementor'
import { v4 as uuidv4 } from 'uuid'
import PaidSocialForm from './experiment/PaidSocialForm'
import { useFlags } from 'gatsby-plugin-launchdarkly'
import { getReportingAdapter } from '../reporting/get-reporting-adapter'
import { EventPrefix, Trigger } from '@pathccm/common-analytics/events'

interface HalfAndHalfPaidSocialProps {
  section: E.Section
  theme?: string
  imageClass?: string
}

interface freshpaint {
  track: Function
}

declare const freshpaint: freshpaint

const HalfAndHalfPaidSocial = ({ section, theme = 'sky', imageClass = '' }: HalfAndHalfPaidSocialProps) => {
  const { heading, textEditor, image, button, html, testimonial } = E.collect(section)

  const form_present = html && html.length > 0 && html[0].html !== null
  const component_id_attr = 'section-half-and-half'

  const backgroundImage = testimonial.length > 0
    ? 'linear-gradient(to bottom, transparent 50%, black 100%), url(' + (image[0]?.image?.fileUrl ?? '') + ')'
    : 'url(' + (image[0]?.image?.fileUrl ?? '') + ')'

  const uuid: string = uuidv4()
  const formRef = createRef<HTMLDivElement>()
  const insuranceExclusionList = ['I Don’t Plan to Use Insurance', "I Don't See Mine", 'Blue Cross Blue Shield', 'Empire Blue Cross Blue Shield']

  const { marketingSiteSegmentTransition } = useFlags()
  const reporting = getReportingAdapter(marketingSiteSegmentTransition)

  const getElementValue = (id: string): string => {
    const element = document.getElementById(id) as HTMLInputElement | HTMLSelectElement
    return element?.value ?? null
  }

  const checkIsMinor = (): boolean => {
    const birthdate = getElementValue('Contact.Birthdate')
    const date = new Date(birthdate)
    const diff = Date.now() - date.getTime()
    const diffDate = new Date(diff)
    const year = diffDate.getUTCFullYear()
    const age = Math.abs(year - 1970)
    const isMinor = Boolean(age < 18)

    return isMinor
  }

  const submitAnalyticsToFreshpaint = (): void => {
    const minor = checkIsMinor()

    const selectedLocation = getElementValue('Contact.Patient_Location__c')

    const selectedInsurance = getElementValue('Contact.Insurance_Carrier__c')

    const therapyInput = document?.querySelector('[data-ff-ext-radio-checked = "true"]') as HTMLSelectElement
    const selectedTherapyType = therapyInput?.getAttribute('data-value')

    const eventData = {
      event: `upsert_${uuid}`,
      minor,
      insurance: selectedInsurance,
      location: selectedLocation,
      therapyType: selectedTherapyType
    }

    if (!insuranceExclusionList.includes(selectedInsurance)) {
      freshpaint.track('patient_insurance_conversion', eventData)
    }
  }

  // check the form wrapper elements, set a click event listener on the 'Next' button to update values
  const checkElements = (mutationsList: MutationRecord[]): void => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        const addedNodes = mutation.addedNodes
        for (const node of addedNodes) {
          if (node instanceof Element && node.id === 'btnnext' && marketingSiteSegmentTransition) {
            const submitButton = document.getElementById('btnsubmit') as HTMLButtonElement
            const continueButton = document.getElementById('btnnext') as HTMLButtonElement
            submitButton?.addEventListener('click', submitAnalyticsToFreshpaint)

            submitButton?.addEventListener('click', () => {
              reporting.track({
                eventName: {
                  prefix: EventPrefix.MARKETING_SITE,
                  trigger: Trigger.FORM_SUBMITTED
                },
                properties: {}
              })
            })

            continueButton?.addEventListener('click', () => {
              reporting.track({
                eventName: {
                  prefix: EventPrefix.MARKETING_SITE,
                  trigger: Trigger.FORM_INTERACTION
                },
                properties: {
                  form_name: 'formstack',
                  interaction_type: 'nextBtnClick',
                }
              })
            })
          }
        }
      }
    }
  }

  useEffect(() => {
    const observer = new MutationObserver(checkElements)
    const config = { childList: true, subtree: true }
    const formWrapper = formRef.current;

    (formWrapper != null) && observer.observe(formWrapper, config)

    return () => {
      observer.disconnect()
    }
  }, [marketingSiteSegmentTransition])

  return (
    <div className={`ps-half-and-half-wrapper ${imageClass}`} id={component_id_attr} data-theme={theme}>
      <div className={`half-and-half-content ${html && html.length > 0 ? 'form' : ''}`}>
        <div className='form-block' ref={formRef}>
            <h2 className='headline'>{parse(heading[0]?.title ?? '')}</h2>
            <p className='subheadline'>{textEditor[0]?.parse()}</p>
            <div className='form-container'>
                <PaidSocialForm />
            </div>
        </div>
      </div>
      <div className={`half-and-half-image ${imageClass}`} style={{ backgroundImage, backgroundSize: 'cover' }}>
        <img className='large-image' src={image[0]?.image?.fileUrl} alt={image[0]?.image?.altText} />
        <img className='logo' src={Logo} alt='Rula Logo' />
        <div className='testimonial'>
          <p className='content'>{parse(testimonial[0]?.testimonials[0]?.content ?? '')}</p>
          <p className='author'>{parse(testimonial[0]?.testimonials[0]?.name ?? '')}</p>
          <p className='title'>{parse(testimonial[0]?.testimonials[0]?.title ?? '')}</p>
        </div>
      </div>
    </div>
  )
}

export default HalfAndHalfPaidSocial
